import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchWorkLogs } from "../api/workLogs";
import { useAuth } from "../hooks/useAuth";
import LoadingSpinner from "../components/LoadingSpinner";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import StatusBadge from "../components/history/StatusBadge";
import MonthSection from "../components/history/MonthSection";
import FilterTabs from "../components/history/FilterTabs";
import SearchBar from "../components/history/SearchBar";
import heroImage from "../assets/Sample/adrien-bruneau-8cpR_Yf0rQs-unsplash.jpg";
import { buildMonthSections, formatWorkDate } from "./workHistoryUtils";
import { WORK_LOG_COPY } from "../components/dashboard/workLogCopy";

const FILTERS = ["all", "approved", "pending", "rejected"];

// One log row. Desktop: Date | Work (or Class) | Hours | Status columns. Mobile: two
// lines — date + badge on top, description + hours below.
function WorkRow({ log }) {
    return (
        <div className="py-[16px] border-b border-[#dfe0e2]">
            <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[130px_1fr_90px_140px] gap-x-4 gap-y-[6px] sm:items-center text-[18px] sm:text-[20px] text-[#5e5e5e]">
                <span className="order-1">{formatWorkDate(log.work_date)}</span>
                <span className="order-2 sm:order-4 justify-self-end sm:justify-self-start">
                    <StatusBadge status={log.status} />
                </span>
                <span className="order-3 sm:order-2 min-w-0 break-words">
                    {log.description}
                </span>
                <span className="order-4 sm:order-3 justify-self-end sm:justify-self-start">
                    {Number(log.hours)}
                </span>
            </div>
            {log.status === "rejected" && log.rejection_reason && (
                <p className="mt-[7px] sm:ml-[146px] text-[16px] text-[#c52e2e]">
                    Reason: {log.rejection_reason}
                </p>
            )}
        </div>
    );
}

export default function WorkHistory({ program = "internship" }) {
    const { isAuthenticated, isLoadingUser } = useAuth();
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");
    const copy = WORK_LOG_COPY[program];

    // Internship and instructor logs -- volunteers have their own Service
    // History page. The route decides the program, never a query parameter.
    const {
        data: workLogs = [],
        isLoading,
        isError,
    } = useQuery({
        queryKey: ["workLogs", program],
        queryFn: () => fetchWorkLogs(program),
        enabled: isAuthenticated,
    });

    if (isLoadingUser || isLoading) return <LoadingSpinner />;

    const sections = buildMonthSections(workLogs, { status: statusFilter, search });

    return (
        <div className="bg-white text-left" style={{ fontFamily: "Arial, sans-serif" }}>
            <PageHero image={heroImage} title={copy.historyTitle} />

            <main className="mt-0 px-6 sm:px-10 xl:px-[83px] pt-[57px] pb-[120px] max-w-[1728px] mx-auto">
                {/* Status filter tabs + search bar */}
                <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-5">
                    <FilterTabs
                        filters={FILTERS}
                        value={statusFilter}
                        onChange={setStatusFilter}
                        ariaLabel="Status filter"
                    />
                    <SearchBar
                        value={search}
                        onChange={setSearch}
                        ariaLabel={copy.historySearch}
                    />
                </div>

                {isError ? (
                    <p className="mt-[80px] text-[24px] text-[#c52e2e]">
                        {copy.historyError}
                    </p>
                ) : sections.length > 0 ? (
                    sections.map((section) => (
                        <MonthSection
                            key={section.label}
                            label={section.label}
                            approvedHours={section.approvedHours}
                            gridClass="grid-cols-[130px_1fr_90px_140px]"
                            columns={["Date", copy.column, "Hours", "Status"]}
                        >
                            {section.items.map((log) => (
                                <WorkRow key={log.id} log={log} />
                            ))}
                        </MonthSection>
                    ))
                ) : (
                    <p className="mt-[80px] text-[24px] text-[#777f88]">
                        {workLogs.length === 0 ? copy.historyEmpty : copy.historyNoMatch}
                    </p>
                )}
            </main>

            <Footer />
        </div>
    );
}
